<template>
  <section class="patternmaker-patternmaker-view-thumbnails">
    <div v-if="enableSwiper" class="patternmaker-patternmaker-view-thumbnails-wrapper">
      <swiper
        :class="['patternmaker-patternmaker-view-thumbnails-wrapper-swiper', {
          'patternmaker-patternmaker-view-thumbnails-wrapper-swiper--no-loop': !swiperOptions.loop
        }]"
        :options="swiperOptions"
        ref="swiper"
        @touchEnd="swatchTouched"
        :auto-update="false"
        :cleanup-styles-on-destroy="true"
      >
        <swiper-slide
          v-for="(item, index) in swatches"
          :class="[
            'patternmaker-patternmaker-view-item',
            {
              active: item.id === currentSwatch,
              disabled: !enabled
            }
          ]"
          :data-swatch-id="item.id"
          :key="index"
        >
          <div class="icon-check" v-show="item.id === currentSwatch"></div>
          <img class="background" v-if="item.thumb"
            :src="item.thumb"
            :alt="item.display_name.default"
            :data-swatch-id="item.id"
          />
          <div v-if="item.color" :data-swatch-id="item.id" class="background" :style="`background-color: ${item.color}`"></div>
        </swiper-slide>
      </swiper>
      <div :class="['patternmaker-patternmaker-view-thumbnails-wrapper-nav-wrapper', { 'hide': !enableCarouselNavigation }]">
        <button type="button" class="patternmaker-patternmaker-view-thumbnails-wrapper-nav-button-previous swatches">
          <span class="icon-arrow-left-indicator-open" slot="button-prev"></span>
        </button>
        <button type="button" class="patternmaker-patternmaker-view-thumbnails-wrapper-nav-button-next swatches">
          <span class="icon-arrow-right-indicator-open" slot="button-next"></span>
        </button>
      </div>
    </div>
    <base-spinner v-else ref="spinner" size="50" position="inherit"></base-spinner>
  </section>
</template>

<script>
import baseSpinner from '@/components/Spinner';
import { Swiper, SwiperSlide } from 'vue-awesome-swiper';

const swiperOptions = {
  slidesPerView: 4.5,
  slidesPerGroup: 1,
  loop: false,
  loopedSlides: 0,
  speed: 300,
  freeMode: true,
  freeModeMomentum: true,
  freeModeMomentumRatio: 0.9,
  breakpoints: {
    375: {
      slidesPerView: 5.5,
    },
    768: {
      slidesPerView: 7,
      freeModeMomentumRatio: 0.75,
    },
  },
  shortSwipes: false,
  watchSlidesProgress: true,
  watchSlidesVisibility: true
};

export default {
  name: 'patternmakerSwatchesCarousel',
  components: {
    baseSpinner,
    Swiper,
    SwiperSlide
  },
  props: {
    swatches: {
      type: Array,
      default: () => []
    },
    currentSwatch: {
      type: [ Number, String ],
      default: null
    },
    enabled: {
      type: Boolean,
      default: false
    },
    slideToSelectedItem: {
      type: Boolean,
      default: false,
    }
  },
  data() {
    return {
      enableCarouselNavigation: true,
      enableSwiper: false,
      swiperOptions: {
        ...swiperOptions,
        navigation: {
          prevEl: '.patternmaker-patternmaker-view-thumbnails-wrapper-nav-button-previous.swatches',
          nextEl: '.patternmaker-patternmaker-view-thumbnails-wrapper-nav-button-next.swatches'
        }
      },
      isClicked: false
    }
  },
  computed: {
    swiper() {
      return this.$refs.swiper.$swiper;
    }
  },
  watch: {
    swatches() {
      this.checkEnableCarouselNavigation();
    },
  },
  methods: {
    checkEnableLoop() {
      let enableLoop;
      const viewportWidth = window.innerWidth;
      const breakpoints = this.swiperOptions.breakpoints ? Object.keys(this.swiperOptions.breakpoints).sort((a, b) => Number(a)-Number(b)) : [];
      if (!breakpoints.length || viewportWidth < Number(breakpoints[0])) {
        enableLoop = this.swatches.length > this.swiperOptions.slidesPerView;
      }
      for (let i = 0; i < breakpoints.length - 1 && !enableLoop; i++) {
        if (viewportWidth >= Number(breakpoints[i]) && viewportWidth < Number(breakpoints[i + 1])) {
          enableLoop = this.swatches.length > this.swiperOptions.breakpoints[breakpoints[i]].slidesPerView;
        }
      }
      if (!enableLoop && breakpoints.length && viewportWidth >= Number(breakpoints[breakpoints.length - 1])) {
        enableLoop = this.swatches.length > this.swiperOptions.breakpoints[breakpoints[breakpoints.length - 1]].slidesPerView;
      }
      return enableLoop;
    },
    checkEnableCarouselNavigation() {
      if (!this.swatches.length) return;
      const viewportWidth = window.innerWidth;
      const enableLoop = this.checkEnableLoop();
      if (this.swiperOptions.loop !== enableLoop) this.swiperOptions.loop = enableLoop;
      if (enableLoop && (this.swatches.length !== this.swiperOptions.loopedSlides)) this.swiperOptions.loopedSlides = this.swatches.length;
      if (enableLoop && this.swatches) this.swiperOptions.loopedSlides = this.swatches.length;
      this.enableCarouselNavigation = enableLoop && viewportWidth >= 768;
      this.enableSwiper = !!this.swatches.length;
    },
    /**
     * @function swatchTouched
     * If the slide is disable it prevents clicks again until the image is loaded(enabled=true)
     * The limits are -1 to 1 to catch the click event.
     * Otherwise the user is swiping the images.
     */
    swatchTouched(event) {
      this.swiper.updateSlides();
      if (this.isClicked || !this.enabled) return;
      this.isClicked = true;
      const diff = this.swiper.touches.startX - this.swiper.touches.currentX;
      if (diff >= -1 && diff <= 1) {
        this.swatchClicked(event);
      }
      this.isClicked = false;
    },
    swatchClicked(event) {
      const id = event.target.getAttribute('data-swatch-id');
      const swatch = this.swatches.find(swatch => swatch.id === id);
      this.$emit('swatch-selected', swatch);
      if (this.$store.getters['utils/useEcomDataLayerFormat']) {
        this.$patternMakerDatalayer.logAnalyticsEvent({action: 'Select Base Pattern', eventLabel: swatch.id});
      }
      let eventAction = 'pattern maker';
      if (this.$store.getters['patternMaker/isSecondComponentFlow']) eventAction += ' - second component';
    },
    /**
     * @function moveCurrentSwatchIntoView
     * When the swatches are loaded and there's already a selected swatch it tries to move it into view
     */
    moveCurrentSwatchIntoView() {
      const currentSwatchIndex = this.currentSwatch ? this.swatches.findIndex(swatch => swatch.id === this.currentSwatch) : -1;
      if (currentSwatchIndex < 0) return;
      else this.$nextTick(() => this.swiper.slideToLoop(currentSwatchIndex));
    }
  },
  created() {
    this.checkEnableCarouselNavigation();
    this.$eventBus.$on('patternmaker-move-current-swatch-into-view', this.moveCurrentSwatchIntoView);
    window.addEventListener('resize', this.checkEnableCarouselNavigation);
  },
  mounted() {
    if (this.swatches.length && this.currentSwatch && this.slideToSelectedItem) this.moveCurrentSwatchIntoView();
  },
  beforeDestroy() {
    this.$eventBus.$off('patternmaker-move-current-swatch-into-view', this.moveCurrentSwatchIntoView);
    window.removeEventListener('resize', this.checkEnableCarouselNavigation);
  },
}
</script>
